// Utilisateurs de la collectivité : liste, création et invitation, rôles, attributions en
// attente de validation (quatre yeux).
import { Bouton, Dialogue, EtatVide, Panneau, PuceEtat, Registre, Tableau, type ColonneTableau } from '@cc/ui';
import { UserPlus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useRoles, useUtilisateurs } from '../../api/requetes';
import type { Utilisateur } from '../../api/types';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { FormulaireUtilisateur } from './FormulaireUtilisateur';
import { GestionRoles } from './GestionRoles';
import commun from '../../commun/commun.module.css';

const STATUTS: Record<string, { ton: 'ok' | 'attente' | 'neutre' | 'retard'; libelle: string }> = {
  active: { ton: 'ok', libelle: 'Active' },
  invitee: { ton: 'attente', libelle: 'Invitation en attente' },
  suspendue: { ton: 'retard', libelle: 'Suspendue' },
};

export function nomUtilisateur(u: Pick<Utilisateur, 'prenoms' | 'nom'>): string {
  return [u.prenoms, u.nom].filter(Boolean).join(' ');
}

export function PageUtilisateurs() {
  const { contexte } = useSession();
  const utilisateurs = useUtilisateurs();
  const roles = useRoles();
  const [creation, setCreation] = useState(false);
  const [selectionne, setSelectionne] = useState<string | null>(null);
  const administrer = peut(contexte, 'utilisateurs', 'administrer');

  const enAttente = useMemo(
    () =>
      (utilisateurs.data ?? []).flatMap((u) =>
        u.roles.filter((r) => r.statut === 'en_attente_validation').map((r) => ({ utilisateur: u, role: r })),
      ),
    [utilisateurs.data],
  );
  const courant = utilisateurs.data?.find((u) => u.appartenance_id === selectionne) ?? null;

  const colonnes: ColonneTableau<Utilisateur>[] = [
    { id: 'nom', entete: 'Nom', cellule: nomUtilisateur, valeurTri: nomUtilisateur, enTeteLigne: true, masquable: false },
    { id: 'identifiant', entete: 'Identifiant', cellule: (u) => u.identifiant, valeurTri: (u) => u.identifiant },
    {
      id: 'statut',
      entete: 'Appartenance',
      cellule: (u) => {
        const s = STATUTS[u.statut_appartenance] ?? { ton: 'neutre' as const, libelle: u.statut_appartenance };
        return <PuceEtat ton={s.ton} libelle={s.libelle} />;
      },
      valeurTri: (u) => u.statut_appartenance,
    },
    {
      id: 'roles',
      entete: 'Rôles',
      cellule: (u) =>
        u.roles.length === 0 ? (
          <span className={commun.na}>aucun</span>
        ) : (
          <span className={commun.permissions}>
            {u.roles.map((r) => (
              <span key={r.role_id}>
                {r.libelle}
                {r.statut === 'en_attente_validation' ? ' (en attente)' : ''}
              </span>
            ))}
          </span>
        ),
    },
    { id: 'mfa', entete: 'Second facteur', cellule: (u) => (u.mfa_actif ? 'Actif' : 'Inactif'), valeurTri: (u) => (u.mfa_actif ? 1 : 0) },
    {
      id: 'actions',
      entete: 'Actions',
      masquable: false,
      cellule: (u) => (
        <Bouton petit variante="secondaire" onClick={() => setSelectionne(u.appartenance_id)} aria-label={`Gérer les rôles de ${nomUtilisateur(u)}`}>
          Gérer les rôles
        </Bouton>
      ),
    },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Administration"
        titre="Utilisateurs"
        description="Agents et élus ayant une appartenance à la collectivité. Seules des métadonnées d'administration sont affichées."
        actions={
          administrer && !creation ? (
            <Bouton icone={UserPlus} onClick={() => setCreation(true)}>
              Nouvel utilisateur
            </Bouton>
          ) : null
        }
      />
      {creation ? (
        <Panneau titre="Nouvel utilisateur">
          <FormulaireUtilisateur onTermine={() => setCreation(false)} />
        </Panneau>
      ) : null}
      <EtatRequete chargement={utilisateurs.isPending} erreur={utilisateurs.error} onReessayer={() => void utilisateurs.refetch()} fonction="La liste des utilisateurs">
        {enAttente.length > 0 ? (
          <Registre
            titre="Attributions en attente de validation"
            compteur={`${enAttente.length} en attente`}
            elements={enAttente.map(({ utilisateur, role }) => ({
              id: `${utilisateur.appartenance_id}-${role.role_id}`,
              reference: role.code,
              objet: `${role.libelle} pour ${nomUtilisateur(utilisateur)}`,
              sousTitre: 'Règle des quatre yeux : validation par une autre personne détenant le droit de valider',
              etat: { ton: 'attente', libelle: 'À valider' },
            }))}
          />
        ) : null}
        {(utilisateurs.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun utilisateur" icone={Users}>
            <p>Aucune appartenance n&apos;est enregistrée pour cette collectivité.</p>
          </EtatVide>
        ) : (
          <Tableau
            legende="Utilisateurs de la collectivité"
            colonnes={colonnes}
            lignes={utilisateurs.data ?? []}
            cleLigne={(u) => u.appartenance_id}
            cleColonnes="utilisateurs"
          />
        )}
      </EtatRequete>
      <Dialogue ouvert={courant !== null} titre={courant ? `Rôles de ${nomUtilisateur(courant)}` : ''} onFermer={() => setSelectionne(null)}>
        {courant ? (
          <EtatRequete chargement={roles.isPending} erreur={roles.error} fonction="La liste des rôles">
            <GestionRoles utilisateur={courant} roles={roles.data ?? []} />
          </EtatRequete>
        ) : null}
      </Dialogue>
    </>
  );
}
